import { Link } from "react-router-dom";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { HardHat, ArrowRight, Clock } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "./FAQ";
import { BLOG_POSTS, formatDate } from "@/lib/blogPosts";

export default function Blog() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Fieldlink NZ Blog",
    url: "https://www.fieldlink.co.nz/blog",
    description: "Practical guides on timesheets, SSSPs, payroll, fleet and job management for NZ civil construction crews.",
    publisher: { "@type": "Organization", name: "Fieldlink NZ", url: "https://www.fieldlink.co.nz" },
    blogPost: BLOG_POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `https://www.fieldlink.co.nz/blog/${p.slug}`,
      datePublished: p.date,
    })),
  };

  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO
        title="Fieldlink Blog — Guides for NZ Civil Construction Crews"
        description="Practical guides on timesheets, SSSPs, Holidays Act payroll, pre-start checklists, Xero and job management — written for New Zealand civil contractors."
        path="/blog"
        jsonLd={jsonLd}
      />

      <MarketingHeader />

      <section className="bg-[#f6f5f1] text-[#142635] border-b border-[#cbd3d5] py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-4">The Fieldlink Blog</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">
            Guides for the crews<br /><span className="text-[#a63c0b]">who build NZ.</span>
          </h1>
          <p className="text-[#465562] text-base sm:text-lg max-w-2xl">
            Plain-English guides on timesheets, SSSPs, Holidays Act payroll, pre-starts and job costing — written for Kiwi civil contractors, not accountants.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-14">
        <div className="grid gap-6" data-testid="blog-post-list">
          {BLOG_POSTS.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group rounded-lg border border-[#cbd3d5] bg-white p-6 md:p-8 hover:border-[#FF5F15]/50 transition-colors block"
              data-testid={`blog-card-${p.slug}`}
            >
              <div className="flex flex-wrap items-center gap-3 mb-3 text-[10px] font-bold uppercase tracking-widest">
                <span className="bg-[#FF5F15] text-[#142635] px-2 py-0.5">{p.tag}</span>
                <span className="text-[#52616c]">{formatDate(p.date)}</span>
                <span className="text-[#52616c] flex items-center gap-1"><Clock className="w-3 h-3" />{p.readMins} min read</span>
              </div>
              <h2 className="font-display font-black uppercase tracking-tight text-xl md:text-2xl leading-tight group-hover:text-[#a63c0b] transition-colors">{p.title}</h2>
              <p className="text-sm text-[#52616c] leading-relaxed mt-2 max-w-3xl">{p.description}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#a63c0b]">
                Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[#e9eeec] border-y border-[#cbd3d5] text-[#142635] text-center py-16">
        <h2 className="font-display font-black uppercase tracking-tight text-3xl mb-3">Run your whole crew from one NZ-made app</h2>
        <p className="text-[#465562] mb-6">Timesheets, SSSPs, fleet, payroll, jobs. 7 days free.</p>
        <Link to="/register" className="inline-block rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 py-4" data-testid="blog-footer-cta">Start free trial →</Link>
      </section>

      <PublicFooter />
    </div>
  );
}
